
import privateAxiosInstance from "../utils/axios/private";
import publicAxiosInstance from "../utils/axios/public";
import { hybridEncrypt, hybridDecrypt } from "../utils/crypto";

export const authApi = {

    /**
     * Login user
     * @param {Object} payload - { email, password }
     */
    async login(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);
            const response = await privateAxiosInstance.post("/user/login/", encryptedPayload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        }
        catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    setTokens: async ({ access, refresh }) => {
        const response = await privateAxiosInstance.post("/user/auth/set-tokens/", {
            access,
            refresh,
        });
        return response;
    },

    async googleLogin(payload) {

        try {

            const encryptedPayload =
                await hybridEncrypt(payload);

            const response =
                await privateAxiosInstance.post(
                    "/user/google-login/",
                    encryptedPayload
                );

            let finalData = response.data;

            if (response.data?.encrypted) {
                finalData =
                    await hybridDecrypt(response.data);
            }

            // -----------------------------------
            // STORE USER INFO LIKE NORMAL LOGIN
            // -----------------------------------
            if (response.status === 200) {

                const userData =
                    finalData?.data || {};
            }

            return {
                status: response.status,
                data: finalData,
                message: finalData?.message,
                code: finalData?.code,
                success: true,
            };

        } catch (error) {

            if (error.code === "ERR_NETWORK") {

                return {
                    status: 0,
                    data: null,
                    message:
                        "Network error. Please check your connection.",
                    success: false,
                };
            }

            return {
                status:
                    error?.response?.status,

                data:
                    error?.response?.data,

                message:
                    error?.response?.data?.message,

                code:
                    error?.response?.data?.code,

                success: false,
            };
        }
    },

    async logout() {
        try {
            const response = await privateAxiosInstance.post("/user/logout/");
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        }
        catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    /**
     * Register new user
     * @param {Object} payload - { name, email, password, role }
     */
    async register(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);
            const response = await privateAxiosInstance.post("/user/register/", encryptedPayload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        }
        catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    /**
     * Forgot password — Step 1: Send OTP
     * @param {Object} payload - { email }
     */
    async forgotPassword(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);
            const response = await publicAxiosInstance.post("/user/forgot-password/", encryptedPayload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        }
        catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    /**
     * Forgot password — Step 2: Verify OTP
     * @param {Object} payload - { email, otp }
     */
    async verifyOtp(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);
            const response = await publicAxiosInstance.post("/user/otp/verify/", encryptedPayload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        }
        catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    /**
     * Reset password
     * @param {Object} payload - { token, new_password }
     */
    async resetPassword(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);
            const response = await publicAxiosInstance.post("/user/reset-password/", encryptedPayload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        }
        catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    /**
     * Refresh authentication token
     * (if your backend supports refresh token rotation)
     */
    async refreshToken(payload) {
        try {
            const encryptedPayload = await hybridEncrypt(payload);
            const response = await publicAxiosInstance.post("/user/refresh/", encryptedPayload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data
        }
        catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },

    /**
     * Check user status + is_password_changed before showing login form.
     * @param {Object} payload - { email }
     * @returns {{ status: string, is_password_changed: boolean }}
     */
    async checkUserStatus(payload) {
        try {
            const response = await publicAxiosInstance.post("/user/check-status/", payload);
            if (response.data.encrypted) {
                return await hybridDecrypt(response.data);
            }
            return response.data;
        } catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                };
            }
            return error?.response?.data ?? { status: 500 };
        }
    },

    async profile() {
        try {
            const response = await privateAxiosInstance.get("/user/profile/");
            if (response.data.encrypted) {
                // console.log(await hybridDecrypt(response.data.data))
                return await hybridDecrypt(response.data);
            }
            return response.data
        }
        catch (error) {
            if (error.code === "ERR_NETWORK") {
                return {
                    status: 0,
                    data: "Network_error",
                    message: "Network error. Please check your internet connection or try again later.",
                    type: "network_error",
                }
            }
            return error.response.data
        }
    },
};
